import { useCallback } from "react";
import cx from "classnames";

import Box from "ds/components/Box";
import Typography from "ds/components/Typography";
import ListEntitiesItemDescription from "components/ListEntitiesItem/Description";
import { StackContextAttachment } from "types/generated";
import Tag from "ds/components/Tag";
import { getAttachedLabel } from "utils/labels";
import { useDrawerVisibilityForId } from "ds/components/DrawerNew/useDrawerVisibilityForId";
import { AccessResult } from "utils/access";
import TextEllipsis from "ds/components/TextEllipsis";

import PriorityTag from "./PriorityTag";
import styles from "./styles.module.css";

type ContextAttachment = Omit<StackContextAttachment, "isAutoattached" | "contextHooks">;

type ManualContextAttachmentProps = {
  context: ContextAttachment & {
    isAutoattached: false;
  };
  stackLabels?: undefined;
  onDetach?: (id: string) => void;
  onPriorityChange?: (id: string, value: number) => void;
};

type AutoContextAttachmentProps = {
  context: ContextAttachment & {
    isAutoattached: true;
  };
  stackLabels: string[];
  onDetach?: undefined;
  onPriorityChange?: undefined;
};

type AllContextAttachmentProps = {
  readonly?: boolean;
  canUpdate?: AccessResult;
} & (ManualContextAttachmentProps | AutoContextAttachmentProps);

const ContextAttachment = ({
  context,
  stackLabels,
  onDetach,
  onPriorityChange,
  readonly,
  canUpdate,
}: AllContextAttachmentProps) => {
  const isActive = useDrawerVisibilityForId(context.id);
  const handleDetach = useCallback(async () => onDetach?.(context.id), [context.id, onDetach]);

  const handlePriorityChange = useCallback(
    async (value: number) => onPriorityChange?.(context.contextId, value),
    [context.contextId, onPriorityChange]
  );

  return (
    <Box
      grid
      gridTemplate="minmax(0, 1fr) min-content"
      gridAreas={`"info priority"`}
      gap="large"
      align="center"
      fullWidth
      className={cx(styles.wrapper, {
        [styles.readonly]: readonly || context.isAutoattached,
        [styles.active]: isActive,
      })}
    >
      <Box gridArea="info" align="center" gap="medium">
        <Box gap="small" direction="column" zeroMinWidth>
          <TextEllipsis tooltip={context.contextName} tooltipWidthMode="maxWidthSm">
            {(props) => (
              <Typography {...props} tag="span" variant="p-t7">
                {context.contextName}
              </Typography>
            )}
          </TextEllipsis>
          {context.contextDescription && (
            <ListEntitiesItemDescription description={context.contextDescription} id={context.id} />
          )}
        </Box>
      </Box>
      <Box gridArea="priority">
        {context.isAutoattached && stackLabels && (
          <Tag tag={getAttachedLabel(stackLabels, context.contextLabels)} withAutoAttach />
        )}
        {!context.isAutoattached &&
          (!readonly ? (
            <PriorityTag
              value={context.priority}
              onChange={handlePriorityChange}
              onDetach={handleDetach}
              canUpdate={canUpdate}
            />
          ) : (
            <Tag tag={`Priority: ${context.priority}`} />
          ))}
      </Box>
    </Box>
  );
};

export default ContextAttachment;
